import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import {
  AlertTriangle,
  Archive,
  Download,
  PlusCircle,
  CheckCircle2,
  ClockAlert,
  Inbox,
  ImageIcon,
  Search,
  SlidersHorizontal,
  Star,
  Timer,
  ChevronRight,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { loadSession } from "../../lib/auth";
import { ServiceIcon } from "../../lib/icons";

interface AnfrageRow {
  id: string;
  reference_number: string;
  status: string;
  service_category: string;
  sub_service: string | null;
  urgency: "notfall" | "bald" | "planen";
  customer_vorname: string;
  customer_nachname: string | null;
  description: string | null;
  kunde_typ?: string | null;
  quelle?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  created_at: string;
  updated_at: string;
}

const COLUMNS: { id: string; label: string; accent: string }[] = [
  { id: "received", label: "Neue Anfragen", accent: "bg-sky-500" },
  { id: "in_review", label: "In Bearbeitung", accent: "bg-amber-500" },
  { id: "inspection_scheduled", label: "Besichtigung", accent: "bg-orange-500" },
  // Fehlte: Karten mit diesem Status verschwanden kommentarlos vom Brett.
  { id: "inspection_done", label: "Besichtigt", accent: "bg-yellow-500" },
  { id: "quote_sent", label: "Angebot gesendet", accent: "bg-violet-500" },
  { id: "quote_declined", label: "Angebot abgelehnt", accent: "bg-rose-500" },
  { id: "quote_approved", label: "Freigegeben", accent: "bg-emerald-500" },
  { id: "scheduled", label: "Eingeplant", accent: "bg-teal-500" },
  { id: "in_progress", label: "In Arbeit", accent: "bg-indigo-500" },
  { id: "completed", label: "Abgeschlossen", accent: "bg-slate-400" },
  // Fehlte ebenfalls - und der Rechnungs-Upload setzt diesen Status automatisch.
  { id: "invoiced", label: "Rechnung gestellt", accent: "bg-slate-500" },
  { id: "paid", label: "Bezahlt", accent: "bg-emerald-600" },
];

// Normalablauf für den "Weiter"-Knopf: die Abzweigung "Angebot abgelehnt"
// ist kein nächster Schritt und wird übersprungen.
const FLOW = COLUMNS.filter((c) => c.id !== "quote_declined");

// Wie weit ist der Vorgang? Nur zum Erkennen von Rückschritten - "Angebot
// abgelehnt" ist eine Abzweigung neben "Angebot gesendet", keine eigene Stufe.
const RANG: Record<string, number> = {
  received: 0, in_review: 1, inspection_scheduled: 2, inspection_done: 3,
  quote_sent: 4, quote_declined: 4, quote_approved: 5, scheduled: 6,
  in_progress: 7, completed: 8, invoiced: 9, paid: 10,
};
const rang = (status: string): number => RANG[status] ?? 0;

const URGENCY: Record<
  string,
  { label: string; classes: string }
> = {
  notfall: { label: "Notfall", classes: "bg-red-50 text-red-700 ring-1 ring-red-200" },
  bald: { label: "Bald", classes: "bg-orange-50 text-orange-700 ring-1 ring-orange-200" },
  planen: { label: "Flexibel", classes: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
};

export function Dashboard() {
  const qc = useQueryClient();
  const [filterUrgency, setFilterUrgency] = useState<string>("");
  const [search, setSearch] = useState("");
  const [archiv, setArchiv] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["anfragen", archiv],
    queryFn: () =>
      api<{ items: AnfrageRow[]; total: number }>(
        `/admin/anfragen?limit=500${archiv ? "&archiv=1" : ""}`,
      ),
  });

  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () =>
      api<{
        ratingAvg: number | null;
        ratingCount: number;
        avgResponseHours: number | null;
      }>("/admin/stats"),
  });

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    return items.filter((a) => {
      if (filterUrgency && a.urgency !== filterUrgency) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${a.customer_vorname} ${a.customer_nachname ?? ""} ${a.reference_number}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, filterUrgency, search]);

  const byColumn = useMemo(() => {
    const map = new Map<string, AnfrageRow[]>();
    for (const col of COLUMNS) map.set(col.id, []);
    for (const row of filtered) map.get(row.status)?.push(row);
    return map;
  }, [filtered]);

  // Möglicherweise doppelt: dieselbe Telefonnummer oder E-Mail in mehr als
  // einem offenen Vorgang. Nur Hinweis, keine Automatik.
  const doppelte = useMemo(() => {
    const zaehler = new Map<string, number>();
    const items = data?.items ?? [];
    for (const a of items) {
      for (const k of [a.customer_phone?.replace(/\s+/g, ""), a.customer_email?.toLowerCase()]) {
        if (k) zaehler.set(k, (zaehler.get(k) ?? 0) + 1);
      }
    }
    const ids = new Set<string>();
    for (const a of items) {
      const tel = a.customer_phone?.replace(/\s+/g, "");
      const mail = a.customer_email?.toLowerCase();
      if ((tel && (zaehler.get(tel) ?? 0) > 1) || (mail && (zaehler.get(mail) ?? 0) > 1)) ids.add(a.id);
    }
    return ids;
  }, [data]);

  const notifications = useMemo(() => {
    const now = Date.now();
    const H24 = 24 * 60 * 60 * 1000;
    const H48 = 48 * 60 * 60 * 1000;
    const D7 = 7 * 24 * 60 * 60 * 1000;
    const items = data?.items ?? [];
    return {
      newSinceYesterday: items.filter(
        (a) => now - new Date(a.created_at).getTime() < H24,
      ).length,
      overdueNew: items.filter(
        (a) =>
          a.status === "received" &&
          now - new Date(a.created_at).getTime() > H48,
      ).length,
      stuckQuotes: items.filter(
        (a) =>
          a.status === "quote_sent" &&
          now - new Date(a.updated_at).getTime() > D7,
      ).length,
      recentlyApproved: items.filter(
        (a) =>
          a.status === "quote_approved" &&
          now - new Date(a.updated_at).getTime() < D7,
      ).length,
    };
  }, [data]);

  const patchStatus = useMutation({
    mutationFn: ({ id, status, zurueck }: { id: string; status: string; zurueck?: boolean }) =>
      api(`/admin/anfragen/${id}`, { method: "PATCH", body: { status, ...(zurueck ? { zurueck: true } : {}) } }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ["anfragen"] });
      const prev = qc.getQueryData<{ items: AnfrageRow[] }>(["anfragen"]);
      if (prev) {
        qc.setQueryData(["anfragen"], {
          ...prev,
          items: prev.items.map((a) => (a.id === id ? { ...a, status } : a)),
        });
      }
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["anfragen"], ctx.prev);
      // Die Karte springt zurück - der Grund muss dazu, sonst wirkt es kaputt.
      alert(e instanceof ApiError ? e.message : "Der Vorgang konnte nicht verschoben werden.");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["anfragen"] }),
  });

  /**
   * Vorgänge als CSV herunterladen. Der Abruf ist angemeldet, deshalb erst
   * holen und dann als Datei speichern - ein einfacher Link trüge die
   * Anmeldung nicht mit.
   */
  async function csvHerunterladen() {
    try {
      const blob = await api<Blob>(`/admin/anfragen/export${archiv ? "?archiv=1" : ""}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `auftraege-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Die Liste konnte nicht erstellt werden.");
    }
  }

  function onDragEnd(result: DropResult) {
    if (!result.destination) return;
    const id = result.draggableId;
    const status = result.destination.droppableId;
    const current = filtered.find((a) => a.id === id);
    if (!current || current.status === status) return;

    // Auf dem Handy ist eine Karte schnell in die falsche Spalte gewischt.
    // Ein Schritt zurück verschickt beim Kunden erneut eine Status-Mail -
    // deshalb hier einmal nachfragen, statt es stillschweigend zu tun.
    const zurueck = rang(status) < rang(current.status);
    if (zurueck) {
      const von = COLUMNS.find((c) => c.id === current.status)?.label ?? current.status;
      const nach = COLUMNS.find((c) => c.id === status)?.label ?? status;
      const ok = window.confirm(
        `„${current.customer_vorname} ${current.customer_nachname ?? ""}" von „${von}" zurück auf „${nach}" setzen?\n\n`
        + "Das ist ein Schritt zurück. Der Kunde bekommt dazu unter Umständen erneut eine E-Mail.",
      );
      if (!ok) {
        // Ohne Neuladen bliebe die Karte optisch in der neuen Spalte liegen.
        qc.invalidateQueries({ queryKey: ["anfragen"] });
        return;
      }
    }
    patchStatus.mutate({ id, status, zurueck });
  }

  const showBanner =
    notifications.newSinceYesterday > 0 ||
    notifications.overdueNew > 0 ||
    notifications.stuckQuotes > 0 ||
    notifications.recentlyApproved > 0;

  return (
    <div>
      <header className="mb-5 flex flex-wrap items-center gap-3">
        <div>
          <h1 className="page-title">
            {loadSession()?.user.role === "staff" ? "Meine Aufträge" : "Anfragen"}
          </h1>
          <p className="flex items-center gap-2 text-sm text-slate-500">
            {filtered.length} {filtered.length === 1 ? "Eintrag" : "Einträge"}
            {loadSession()?.user.role === "staff" && (
              <span className="text-slate-400">· nur Ihnen zugewiesene Vorgänge</span>
            )}
            {stats?.ratingAvg != null && stats.ratingCount > 0 && (
              <span className="pill bg-amber-50 text-amber-800 ring-1 ring-amber-200">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                {stats.ratingAvg.toLocaleString("de-DE")} · {stats.ratingCount}{" "}
                {stats.ratingCount === 1 ? "Bewertung" : "Bewertungen"}
              </span>
            )}
            {stats?.avgResponseHours != null && (
              <span className="pill bg-sky-50 text-sky-800 ring-1 ring-sky-200">
                <Timer className="h-3 w-3" />Ø Reaktionszeit:{" "}
                {formatHours(stats.avgResponseHours)}
              </span>
            )}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Suchen…"
              className="input w-72 pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="relative">
            <SlidersHorizontal className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <select
              className="input w-52 pl-9"
              value={filterUrgency}
              onChange={(e) => setFilterUrgency(e.target.value)}
            >
              <option value="">Alle Dringlichkeiten</option>
              <option value="notfall">Notfall</option>
              <option value="bald">Bald</option>
              <option value="planen">Flexibel</option>
            </select>
          </div>
          <Link to="/app/anfragen/neu" className="btn-primary">
            <PlusCircle className="h-4 w-4" /> Erfassen
          </Link>
          <button
            type="button"
            onClick={() => setArchiv((v) => !v)}
            className={archiv ? "btn-primary" : "btn-secondary"}
          >
            <Archive className="h-4 w-4" /> Archiv
          </button>
          {/* Für Excel, die Buchhaltung oder den Steuerberater. */}
          <button
            type="button"
            onClick={csvHerunterladen}
            className="btn-secondary"
            title="Alle Vorgänge als Tabelle für Excel herunterladen"
          >
            <Download className="h-4 w-4" /> Excel-Liste
          </button>
        </div>
      </header>

      {archiv ? (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-3">Vorgang</th>
                <th className="px-4 py-3">Kunde</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Zuletzt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-slate-500">Das Archiv ist leer.</td></tr>
              )}
              {filtered.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/app/anfragen/${a.id}`} className="font-medium text-slate-900 hover:underline">
                      {a.reference_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{a.customer_vorname} {a.customer_nachname ?? ""}</td>
                  <td className="px-4 py-3">{COLUMNS.find((c) => c.id === a.status)?.label ?? (a.status === "cancelled" ? "Abgesagt" : a.status)}</td>
                  <td className="px-4 py-3 text-slate-500">{new Date(a.updated_at).toLocaleDateString("de-DE")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <>
      {showBanner && (
        <div className="mb-5 flex flex-wrap gap-2">
          {notifications.newSinceYesterday > 0 && (
            <NotificationPill
              tone="bg-sky-50 text-sky-800 ring-sky-200"
              Icon={Inbox}
              label={`${notifications.newSinceYesterday} ${notifications.newSinceYesterday === 1 ? "neue Anfrage" : "neue Anfragen"} seit gestern`}
            />
          )}
          {notifications.overdueNew > 0 && (
            <NotificationPill
              tone="bg-red-50 text-red-800 ring-red-200"
              Icon={AlertTriangle}
              label={`${notifications.overdueNew} ${notifications.overdueNew === 1 ? "Anfrage" : "Anfragen"} seit 48 Std. ohne Bearbeitung`}
            />
          )}
          {notifications.stuckQuotes > 0 && (
            <NotificationPill
              tone="bg-orange-50 text-orange-800 ring-orange-200"
              Icon={ClockAlert}
              label={`${notifications.stuckQuotes} ${notifications.stuckQuotes === 1 ? "Angebot" : "Angebote"} > 7 Tage ohne Rückmeldung`}
            />
          )}
          {notifications.recentlyApproved > 0 && (
            <NotificationPill
              tone="bg-emerald-50 text-emerald-800 ring-emerald-200"
              Icon={CheckCircle2}
              label={`${notifications.recentlyApproved} ${notifications.recentlyApproved === 1 ? "Angebot" : "Angebote"} kürzlich freigegeben`}
            />
          )}
        </div>
      )}

      {isLoading ? (
        <BoardSkeleton />
      ) : (
        <>
        {/* Handy: Chips zum Springen zwischen den Spalten - wischen geht auch,
            aber mit 10 Spalten ist ein direkter Sprung schneller. */}
        <div className="-mx-1 mb-2 flex gap-1.5 overflow-x-auto px-1 pb-1 md:hidden">
          {COLUMNS.map((col) => {
            const n = byColumn.get(col.id)?.length ?? 0;
            return (
              <button
                key={col.id}
                type="button"
                onClick={() =>
                  document.getElementById(`spalte-${col.id}`)?.scrollIntoView({
                    behavior: "smooth", inline: "center", block: "nearest",
                  })
                }
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${
                  n > 0
                    ? "border-slate-300 bg-white text-slate-700"
                    : "border-slate-200 bg-slate-50 text-slate-400"
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${col.accent}`} />
                {col.label}
                {n > 0 && <span className="font-semibold">{n}</span>}
              </button>
            );
          })}
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 md:snap-none">
            {COLUMNS.map((col) => (
              <Droppable droppableId={col.id} key={col.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    id={`spalte-${col.id}`}
                    className={`flex min-h-[60vh] w-[86vw] max-w-[320px] flex-shrink-0 snap-center flex-col rounded-2xl border border-slate-200 bg-slate-50/70 p-3 transition-shadow sm:w-[300px] md:snap-align-none ${
                      snapshot.isDraggingOver
                        ? "shadow-elevated ring-2 ring-brand/20"
                        : ""
                    }`}
                  >
                    <div className="mb-3 flex items-center justify-between px-1">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${col.accent}`} />
                        <h3 className="text-sm font-semibold text-slate-800">
                          {col.label}
                        </h3>
                      </div>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200">
                        {byColumn.get(col.id)?.length ?? 0}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {(byColumn.get(col.id) ?? []).map((a, idx) => (
                        <Draggable draggableId={a.id} index={idx} key={a.id}>
                          {(p) => {
                            // "Abgelehnt" ist eine Abzweigung, kein nächster
                            // Schritt: der Weiter-Knopf folgt dem Normalablauf.
                            const i = FLOW.findIndex((c) => c.id === a.status);
                            const naechste = i >= 0 && i < FLOW.length - 1 ? FLOW[i + 1] : null;
                            return (
                              <Card
                                a={a}
                                provided={p}
                                doppelt={doppelte.has(a.id)}
                                onWeiter={naechste
                                  ? () => patchStatus.mutate({ id: a.id, status: naechste.id })
                                  : null}
                              />
                            );
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
        </>
      )}
      </>
      )}
    </div>
  );
}

function Card({
  a,
  provided,
  onWeiter,
  doppelt,
}: {
  a: AnfrageRow;
  provided: Parameters<
    Parameters<typeof Draggable>[0]["children"]
  >[0];
  /** Karte eine Spalte weiterschieben - Ersatz fürs Ziehen auf dem Handy. */
  onWeiter?: (() => void) | null;
  /** Telefon/E-Mail taucht in mehreren offenen Vorgängen auf. */
  doppelt?: boolean;
}) {
  const sla = slaLevel(a);
  const urg = URGENCY[a.urgency];
  return (
    <Link
      to={`/app/anfragen/${a.id}`}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      className={`group block rounded-xl border bg-white p-3.5 text-sm shadow-soft transition-all duration-150 hover:shadow-elevated ${sla.border}`}
    >
      <div className="mb-1.5 flex items-center justify-between gap-1">
        <span className="truncate font-semibold text-slate-900">
          {a.customer_vorname} {a.customer_nachname ?? ""}
        </span>
        {doppelt && (
          <span className="pill shrink-0 bg-amber-50 text-amber-700 ring-1 ring-amber-200" title="Telefon oder E-Mail taucht in mehreren offenen Vorgängen auf">
            evtl. doppelt
          </span>
        )}
        {a.quelle && a.quelle !== "widget" && (
          <span className="pill shrink-0 bg-sky-50 text-sky-700 ring-1 ring-sky-200" title="Von Hand erfasst">
            {a.quelle === "telefon" ? "Telefon" : a.quelle === "whatsapp" ? "WhatsApp" : "Persönlich"}
          </span>
        )}
        {a.kunde_typ === "stammkunde" && (
          <span className="pill shrink-0 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" title="Stammkunde - war schon einmal Kunde">
            Stammkunde
          </span>
        )}
        {urg && (
          <span className={`pill shrink-0 ${urg.classes}`}>{urg.label}</span>
        )}
      </div>
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <ServiceIcon id={a.service_category} className="h-3.5 w-3.5 text-slate-500" />
        <span className="truncate">
          {a.sub_service ?? a.service_category}
        </span>
      </p>
      {a.description && (
        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-slate-500">
          {a.description}
        </p>
      )}
      <div className="mt-2.5 flex items-center justify-between">
        <p className="text-[11px] text-slate-400">
          {timeAgo(a.status === "received" ? a.created_at : a.updated_at)}
        </p>
        {sla.badge && (
          <span className={`pill ${sla.badge.tone}`}>
            <sla.badge.Icon className="h-3 w-3" />
            {sla.badge.label}
          </span>
        )}
      </div>
      {/* Handy: Ziehen zwischen Spalten ist auf Touch mühsam - ein Tipp
          schiebt die Karte in die nächste Spalte. */}
      {onWeiter && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onWeiter(); }}
          className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 py-2 text-xs font-medium text-slate-600 active:bg-slate-100 md:hidden"
        >
          Weiter <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </Link>
  );
}

function NotificationPill({
  tone,
  Icon,
  label,
}: {
  tone: string;
  Icon: typeof Inbox;
  label: string;
}) {
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ring-1 ${tone}`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {COLUMNS.map((col) => (
        <div
          key={col.id}
          className="min-h-[60vh] w-[86vw] max-w-[320px] flex-shrink-0 rounded-2xl border border-slate-200 bg-slate-50/70 p-3 sm:w-[300px]"
        >
          <div className="mb-3 h-4 w-1/2 rounded bg-slate-200" />
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-24 rounded-xl border border-slate-200 bg-white"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function slaLevel(row: {
  status: string;
  created_at: string;
  updated_at: string;
}): {
  border: string;
  badge?: {
    tone: string;
    label: string;
    Icon: typeof AlertTriangle;
  };
} {
  const now = Date.now();
  const H24 = 24 * 60 * 60 * 1000;
  const D7 = 7 * 24 * 60 * 60 * 1000;

  if (row.status === "received") {
    const age = now - new Date(row.created_at).getTime();
    if (age > 2 * H24)
      return {
        border: "border-red-300 ring-1 ring-red-100",
        badge: {
          tone: "bg-red-50 text-red-700 ring-1 ring-red-200",
          label: "überfällig",
          Icon: AlertTriangle,
        },
      };
    if (age > H24)
      return {
        border: "border-orange-300",
        badge: {
          tone: "bg-orange-50 text-orange-700 ring-1 ring-orange-200",
          label: "Warnung",
          Icon: ClockAlert,
        },
      };
  }
  if (row.status === "quote_sent") {
    const age = now - new Date(row.updated_at).getTime();
    if (age > D7)
      return {
        border: "border-red-300 ring-1 ring-red-100",
        badge: {
          tone: "bg-red-50 text-red-700 ring-1 ring-red-200",
          label: "Nachfassen",
          Icon: ClockAlert,
        },
      };
  }
  return { border: "border-slate-200" };
}

function formatHours(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} Min`;
  if (hours < 48) return `${hours.toLocaleString("de-DE")} Std`;
  return `${Math.round(hours / 24)} Tage`;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 60) return `vor ${min} Min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `vor ${hr} Std`;
  const days = Math.round(hr / 24);
  return `vor ${days} Tag${days === 1 ? "" : "en"}`;
}
